import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { Alert, CheckCircle, Error, Info, Warning } from '@mui/icons-material';
import { useTheme } from '@mui/material';

const NotificationContext = createContext();

export function useNotification() {
  return useContext(NotificationContext);
}

const iconMap = {
  info: <Info fontSize="small" />,
  warning: <Warning fontSize="small" />,
  success: <CheckCircle fontSize="small" />,
  error: <Error fontSize="small" />,
};


export function NotificationProvider({ children }) {
  const [notifications, setNotifications] = useState([]);

  const addNotification = useCallback((variant, message, duration = 5000) => {
    const id = Date.now();
    setNotifications((prev) => [...prev, { id, variant, message, duration }]);
  }, []);

  const removeNotification = useCallback((id) => {
    setNotifications((prev) => prev.filter(notification => notification.id !== id));
  }, []);

  return (
    <NotificationContext.Provider value={addNotification}>
      {children}
      <div style={{ position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)', zIndex: 1400 }}>
        {notifications.map(({ id, variant, message, duration }) => (
          <Notification key={id} id={id} variant={variant} message={message} duration={duration} onClose={removeNotification} />
        ))}
      </div>
    </NotificationContext.Provider>
  );
}

function Notification({ id, message, variant, duration, onClose }) {
  const [progress, setProgress] = useState(100);
  const [isVisible, setIsVisible] = useState(true);
  const theme = useTheme();

  const colors = {
    info: `linear-gradient(to right, ${theme.palette.notification.blue_light}, ${theme.palette.notification.blue_dark})`,
    warning: `linear-gradient(to right, ${theme.palette.notification.orange_light}, ${theme.palette.notification.orange_dark})`,
    success: `linear-gradient(to right, ${theme.palette.notification.green_light}, ${theme.palette.notification.green_dark})`,
    error: `linear-gradient(to right, ${theme.palette.notification.red_light}, ${theme.palette.notification.red_dark})`,
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setProgress((oldProgress) => {
        if (oldProgress <= 0) {
          clearInterval(timer);
          setIsVisible(false);
          setTimeout(() => onClose(id), 300); 
          return 0;
        }
        const step = 100 / (duration / 10);
        return Math.max(oldProgress - step, 0);
      });
    }, 10);

    return () => clearInterval(timer);
  }, [id, duration, onClose]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.palette.tertiary.main,
        padding: '16px',
        borderRadius: '8px',
        boxShadow: `0 4px 6px ${theme.palette.tertiary.shadow_address}`,
        marginBottom: '8px',
        width: '300px',
        opacity: isVisible ? 1 : 0,
        transition: 'opacity 0.3s ease-in-out',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div style={{
          marginRight: '16px',
          background: colors[variant],
          borderRadius: '50%',
          padding: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          {iconMap[variant]}
        </div>
        <div style={{ flex: 1 }}>
          <p style={{ margin: 0, fontWeight: 600 }}>{message}</p>
        </div>
        <button
          onClick={() => setIsVisible(false)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.palette.primary.gray }}
        >
          <Error fontSize="small" />
        </button>
      </div>
      <div style={{
        width: '100%',
        height: '4px',
        backgroundColor: theme.palette.primary.light_gray,
        borderRadius: '4px',
        overflow: 'hidden',
        marginTop: '8px',
      }}>
        <div
          style={{
            width: `${progress}%`,
            height: '100%',
            background: colors[variant],
            transition: 'width 0.1s linear',
          }}
        />
      </div>
    </div>
  );
}